import { experimental_useObject as useObject } from '@ai-sdk/react';
import { createOpenSCAD } from 'openscad-wasm-prebuilt';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import * as THREE from 'three';
import { structuredOutputSchema } from './message_type';
import { useState, useCallback, useEffect } from 'react';
import { UIMessage as Message } from 'ai';

// Types
interface Chat3DProject {
  id: string;
  projectId: string;
  name: string;
  latestCode: string | null;
  messages: Chat3DMessage[];
}

interface LogEntry {
  type: 'error' | 'warning' | 'info';
  source: 'openscad' | 'system';
  message: string;
  timestamp: number;
}

interface Chat3DMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  experimental_attachments?: Array<{
    name: string;
    url: string;
    contentType: string;
  }>;
}

interface Attachment {
  name: string;
  url: string;
  contentType: string;
}

interface Chat3DState {
  // Project state
  project: Chat3DProject | null;
  projects: Chat3DProject[];
  isProjectsLoading: boolean;

  // Code and rendering state
  code: string;
  stlData: string | null;
  isRendering: boolean;

  // Logs state
  logs: LogEntry[];

  // Measurement state
  measureMode: boolean;
  measurePoints: THREE.Vector3[];

  // Project actions
  setProject: (project: Chat3DProject | null) => void;
  setProjects: (projects: Chat3DProject[]) => void;
  loadProjects: () => Promise<void>;
  createProject: (name: string) => Promise<Chat3DProject>;
  renameProject: (projectId: string, newName: string) => Promise<void>;

  // Code and rendering actions
  setCode: (code: string) => void;
  updateCode: (code: string) => Promise<void>;
  renderCode: (code: string) => Promise<void>;
  uploadAndPrepareAttachments: (files: File[]) => Promise<Attachment[]>;

  // Log actions
  clearLogs: () => void;
  addLog: (log: LogEntry) => void;

  // Measurement actions
  toggleMeasureMode: () => void;
  clearMeasurements: () => void;
  addMeasurementPoint: (point: THREE.Vector3) => void;
}

// Create a hook to manage chat state using the ai package
export function useChatState(project: Chat3DProject | null) {
  // Define the type for the AI response
  type AIResponse = {
    code: string;
    message: string;
    title?: string;
  };

  // Always initialize hooks regardless of project being null
  // Local state for chat UI
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isProjectLoading, setIsProjectLoading] = useState(project === null);
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);

  // Set up the object generation with default values when project is null
  const { object, submit, stop } = useObject({
    api: project ? `/api/chat3d/projects/${project.projectId}/ai` : '/api/placeholder',
    id: project?.projectId || 'placeholder',
    schema: structuredOutputSchema,
    onFinish: async result => {
      if (!project) return; // Early return if project is null

      // When we get a response, the project is ready
      setIsProjectLoading(false);

      try {
        if (result.object) {
          const { code, message, title } = result.object;

          // Update the code in the UI if code is provided
          if (code) {
            await useChat3DStore.getState().updateCode(code);
            await useChat3DStore.getState().renderCode(code);
          }

          // Update the project name if title is provided
          if (title && project) {
            await useChat3DStore.getState().renameProject(project.projectId, title);
          }

          // Create a structured content object and stringify it
          const contentObject = { message, code, title };
          const contentString = JSON.stringify(contentObject);

          // Add the assistant message to the local messages state
          setMessages(prev => [
            ...prev,
            {
              id: Math.random().toString(36).substring(7),
              role: 'assistant' as const,
              content: contentString,
              parts: [{ type: 'text', text: contentString }],
            } as Message,
          ]);

          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error in onFinish:', error);
        setIsLoading(false);
      }
    },
  });

  // Effect to update project loading state
  useEffect(() => {
    setIsProjectLoading(project === null);
  }, [project]);

  // Effect to update messages when project changes
  useEffect(() => {
    if (project && project.messages) {
      console.log('Loading messages from project:', project.messages);

      // Convert Chat3DMessage[] to Message[]
      const convertedMessages = project.messages.map(msg => {
        // For assistant messages, ensure content is a string
        let content = (msg as any).content;

        // If content is an object, stringify it
        if (typeof content === 'object') {
          content = JSON.stringify(content);
        }

        if (msg.role === 'assistant') {
          try {
            // Check if it's already a valid JSON string
            JSON.parse(content);
          } catch (e) {
            // If it's not a valid JSON string, it might be a plain message
            // Wrap it in a JSON object with a message property
            content = JSON.stringify({ message: content });
          }
        }

        return {
          id: msg.id,
          role: msg.role,
          content: content,
          parts: [{ type: 'text', text: typeof content === 'string' ? content : JSON.stringify(content) }],
          ...(msg.experimental_attachments && {
            experimental_attachments: msg.experimental_attachments,
          }),
        } as Message;
      });

      console.log('Converted messages:', convertedMessages);
      setMessages(convertedMessages);
    }
  }, [project]);

  // Handle input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setInput(e.target.value);
    },
    []
  );

  // Check if the last message has attachments
  const hasAttachments = useCallback(() => {
    if (messages.length === 0) return false;
    const lastMessage = messages[messages.length - 1];
    return (
      lastMessage.role === 'user' &&
      !(lastMessage as any).content &&
      (lastMessage as any).experimental_attachments?.length > 0
    );
  }, [messages]);

  // Handle form submission
  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!project) return; // Early return if project is null

      // Don't submit if there's no input and no pending attachments
      if (!input.trim() && pendingAttachments.length === 0) return;

      // Add user message to local state with any pending attachments
      const userMessage = {
        id: Math.random().toString(36).substring(7),
        role: 'user' as const,
        content: input || 'Image attached', // Use a default message if only attachments
        parts: [{ type: 'text', text: input || 'Image attached' }],
        ...(pendingAttachments.length > 0 && {
          experimental_attachments: pendingAttachments,
        }),
      } as Message;

      setMessages(prev => [...prev, userMessage]);

      // Submit to AI
      setIsLoading(true);

      // Format the input as an object with messages array as expected by the API
      // Ensure all message content is a string
      submit({
        messages: [
          ...messages.map(msg => ({
            role: msg.role,
            content:
              typeof (msg as any).content === 'object'
                ? JSON.stringify((msg as any).content)
                : typeof (msg as any).content !== 'string'
                  ? String((msg as any).content)
                  : (msg as any).content,
          })),
          {
            role: 'user',
            content: input || 'Image attached', // Use a default message if only attachments
          },
        ],
      });

      // Clear input and pending attachments
      setInput('');
      setPendingAttachments([]);
    },
    [input, submit, project, messages, pendingAttachments]
  );

  // Attach images function - now just stores them for the next message
  const attachImages = useCallback(
    async (files: File[]) => {
      if (!project || !files.length) return;

      try {
        // Upload the files and get attachments
        const attachments = await useChat3DStore.getState().uploadAndPrepareAttachments(files);
        console.log('Attachments prepared:', attachments);

        // Store the attachments for the next message
        console.log('Current pendingAttachments before update:', pendingAttachments);
        setPendingAttachments(prev => {
          const newAttachments = [...prev, ...attachments];
          console.log('New pendingAttachments after update:', newAttachments);
          return newAttachments;
        });

        // Force a re-render by setting a dummy state
        setTimeout(() => {
          console.log('Checking pendingAttachments after update:', pendingAttachments);
        }, 100);
      } catch (error) {
        console.error('Error in attachImages:', error);
      }
    },
    [project]
  );

  // If project is null, return a default object with all the expected properties
  if (!project) {
    return {
      object,
      isLoading: isProjectLoading,
      submit: () => {},
      stop: () => {},
      attachImages: async () => {},
      messages: [],
      input: '',
      handleInputChange: () => {},
      handleSubmit: (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
      },
      pendingAttachments: [],
      setPendingAttachments: () => {},
    };
  }

  return {
    object,
    isLoading: isLoading || isProjectLoading,
    submit,
    stop,
    attachImages,
    // Additional properties needed by the UI components
    messages,
    input,
    handleInputChange,
    handleSubmit,
    pendingAttachments,
    setPendingAttachments,
  };
}

export const useChat3DStore = create<Chat3DState>()(
  devtools((set, get) => ({
    // Initial state
    project: null,
    projects: [],
    isProjectsLoading: false,
    code: '',
    stlData: null,
    isRendering: false,
    measureMode: false,
    measurePoints: [],
    logs: [],

    // Project actions
    setProject: project => {
      console.log('Setting project:', project);
      set({ project });
    },
    setProjects: projects => set({ projects }),

    loadProjects: async () => {
      set({ isProjectsLoading: true });
      try {
        console.log('Loading projects...');
        const response = await fetch('/api/chat3d/projects');
        if (!response.ok) {
          console.error(`Failed to load projects: ${response.status} ${response.statusText}`);
          throw new Error('Failed to load projects');
        }
        const data = await response.json();
        console.log('Projects loaded:', data);
        set({ projects: data, isProjectsLoading: false });

        // Log the current state after setting projects
        setTimeout(() => {
          console.log('Current state after loading projects:', get());
        }, 0);

        return data;
      } catch (error) {
        console.error('Error loading projects:', error);
        set({ isProjectsLoading: false, projects: [] });
        return [];
      }
    },

    createProject: async name => {
      try {
        const response = await fetch('/api/chat3d/projects', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        });

        if (!response.ok) throw new Error('Failed to create project');
        const project = await response.json();

        // Refresh projects list
        get().loadProjects();

        return project;
      } catch (error) {
        console.error('Error creating project:', error);
        throw error;
      }
    },

    renameProject: async (projectId, newName) => {
      try {
        console.log(`Renaming project ${projectId} to "${newName}"`);
        // Update local state optimistically first for better UX
        const { project, projects } = get();
        console.log('Current project:', project);
        console.log('Current projects:', projects);

        // Update current project if it's the one being renamed
        if (project && project.projectId === projectId) {
          console.log('Updating current project name');
          set({ project: { ...project, name: newName } });
        }

        // Update projects list
        const updatedProjects = projects.map(p =>
          p.projectId === projectId ? { ...p, name: newName } : p
        );
        console.log('Updated projects:', updatedProjects);
        set({ projects: updatedProjects });

        // Then send the update to the server
        const response = await fetch(`/api/chat3d/projects/${projectId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newName }),
        });

        if (!response.ok) {
          throw new Error('Failed to rename project');
        }
        console.log('Project renamed successfully on server');

        // No need to refresh projects from server since we've already updated locally
      } catch (error) {
        console.error('Error renaming project:', error);

        // Refresh projects to revert to the original state in case of error
        await get().loadProjects();

        throw error;
      }
    },

    // Code and rendering actions
    setCode: code => set({ code }),

    updateCode: async code => {
      const { project } = get();
      if (!project) return;

      try {
        await fetch(`/api/chat3d/projects/${project.projectId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ latestCode: code }),
        });
        set({ code });
      } catch (error) {
        console.error('Error updating code:', error);
      }
    },

    renderCode: async code => {
      set({ isRendering: true });
      set({ logs: [] }); // Clear logs before new render
      try {
        const compiler = await createOpenSCAD();
        const stl = await compiler.renderToStl(code);

        // Add success log
        const successLog: LogEntry = {
          type: 'info',
          source: 'openscad',
          message: 'Successfully rendered OpenSCAD code',
          timestamp: Date.now(),
        };

        set(state => ({
          stlData: stl,
          isRendering: false,
          logs: [...state.logs, successLog],
        }));
      } catch (error) {
        // Add error log
        const errorLog: LogEntry = {
          type: 'error',
          source: 'openscad',
          message: error instanceof Error ? error.message : 'Unknown error during rendering',
          timestamp: Date.now(),
        };

        set(state => ({
          isRendering: false,
          logs: [...state.logs, errorLog],
        }));
      }
    },

    uploadAndPrepareAttachments: async files => {
      // Upload images
      try {
        console.log(`Starting upload of ${files.length} files`);

        const attachments = await Promise.all(
          files.map(async (file, index) => {
            console.log(
              `Uploading file ${index + 1}/${files.length}: ${file.name} (${file.size} bytes)`
            );

            const formData = new FormData();
            formData.append('file', file);

            try {
              const response = await fetch('/api/upload', {
                method: 'POST',
                body: formData,
              });

              if (!response.ok) {
                const errorText = await response.text();
                console.error(`Upload failed with status ${response.status}: ${errorText}`);
                throw new Error(`Upload failed with status ${response.status}: ${errorText}`);
              }

              const data = await response.json();

              if (!data.url) {
                console.error('Upload response missing URL:', data);
                throw new Error('Upload response missing URL');
              }

              console.log(`Successfully uploaded ${file.name}, received URL: ${data.url}`);

              return {
                name: file.name,
                url: data.url,
                contentType: file.type,
              };
            } catch (error) {
              console.error(`Error uploading file ${file.name}:`, error);
              throw error;
            }
          })
        );

        console.log('All files uploaded successfully:', attachments);
        return attachments;
      } catch (error) {
        console.error('Error in uploadAndPrepareAttachments:', error);
        throw error; // Re-throw to handle in the UI
      }
    },

    // Log actions
    clearLogs: () => set({ logs: [] }),
    addLog: (log: LogEntry) => set(state => ({ logs: [...state.logs, log] })),

    // Measurement actions
    toggleMeasureMode: () => set(state => ({ measureMode: !state.measureMode })),
    clearMeasurements: () => set({ measurePoints: [] }),
    addMeasurementPoint: point =>
      set(state => ({
        measurePoints: [...state.measurePoints, point].slice(0, 2), // Keep only last 2 points
      })),
  }))
);
